import { Input } from "@xola/ui-kit";
import { usePreventScrollPropagation } from "common";
import { useField } from "formik";
import { isString } from "lodash";
import React, { InputHTMLAttributes, useContext, useRef } from "react";
import { FormFieldContext, useError } from "./FormField";

// Let's leave these here for a while, just in case.
// I'm still trying to figure out the best way to type regular HTML elements.
// type InputProps = Partial<HTMLInputElement>;
// type InputProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    as?: (props: any) => JSX.Element;
    isRequired?: boolean;

    /** When type is `number`, do we want to allow zero as an input? Defaults to `false` */
    allowZero?: boolean;

    /** When type is `text`, do we want to trim the input string or not ? Defaults to `true` */
    trim?: boolean;
    onChange?: (value: any) => void;
}

export const InputField = ({
    as: Tag = Input,
    isRequired,
    type,
    allowZero = false,
    trim: shouldTrim = true,
    min = undefined,
    onChange,
    ...rest
}: InputProps) => {
    const name = useContext(FormFieldContext);
    const [field, , helpers] = useField(name);
    const error = useError(name);

    const inputRef = useRef<HTMLInputElement>(null);
    usePreventScrollPropagation({ inputRef });

    const isNumberAndAllowsZero = type === "number" && allowZero;
    const fieldValueOrDefault = field.value ?? "";

    const value = isNumberAndAllowsZero ? fieldValueOrDefault : field.value || "";

    const handleBlur = (e: any) => {
        const fieldValue = isString(field.value) && shouldTrim ? field.value.trim() : field.value;
        helpers.setValue(fieldValue);
        field.onBlur(e);
        onChange?.(fieldValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (type !== "number") return;

        const allowedKeys = ["e", "E", "Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
        const canBeNegative = !min || Number(min) < 0;

        if (e.key === "-" && canBeNegative) {
            if (!field.value || field.value.toString().length === 0) {
                return;
            } else {
                e.preventDefault();
                return;
            }
        }

        if (e.key === ".") {
            if (field.value?.toString().includes(".")) {
                e.preventDefault();
                return;
            }
            return;
        }

        if (/[0-9]/.test(e.key) || allowedKeys.includes(e.key)) return;

        e.preventDefault();
    };

    return (
        <Tag
            ref={inputRef}
            isRequired={isRequired}
            isError={!!error}
            {...rest}
            {...field}
            type={type}
            value={value}
            min={min}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
        />
    );
};
